/**
 * Content moderation service — calls the Awesome Chat API's
 * OpenAI omni-moderation endpoint to check user-generated content
 * (text and/or images) before publishing.
 *
 * Endpoint: POST /api/moderation/check
 * Backend: awesome-chat-api (Python, Litestar)
 *
 * Поведение при сбое: НЕ ПРОПУСКАЕМ (CORE-196).
 *   Раньше при недоступности сервиса публикация проходила с предупреждением
 *   в консоль — по решению владельца «редкий непроверенный пост дешевле, чем
 *   заблокированное авторство». Решение пересмотрено: непроверенное
 *   содержимое — это и есть та дыра, ради закрытия которой модерация
 *   существует, и открывается она ровно тогда, когда за ней никто не следит.
 *
 *   Отказ из-за сбоя отделён от отказа из-за нарушения: человеку показывают
 *   «попробуйте ещё раз», а не «вы нарушили правила», и написанное им не
 *   пропадает.
 */

// Same awesome-chat-api host as CHAT_API_BASE_URL; per-env via the Vercel var
// (dev Preview → chat-api.agdev.tmless.org) — CORE-371.
const MODERATION_API_URL =
  process.env.EXPO_PUBLIC_CHAT_API_URL || 'https://chat-api.agdev.tmless.org';

const REQUEST_TIMEOUT_MS = 10_000;

// ── Types ────────────────────────────────────────────────────

export interface ModerationViolation {
  /** OpenAI category e.g. "harassment", "hate", "violence/graphic" */
  category: string;
  /** Human-readable description of the category */
  description: string;
  /** Confidence score 0.0–1.0 (only scores >= 0.5 are returned) */
  score: number;
}

export interface ModerationResult {
  /** Whether any category exceeded the threshold */
  flagged: boolean;
  /** Specific violations found, sorted by score descending */
  violations: ModerationViolation[];
}

// ── Human-readable category labels (from OpenAI docs) ────────
export const CATEGORY_LABELS: Record<string, string> = {
  'harassment': 'Harassment',
  'harassment/threatening': 'Harassment (Threatening)',
  'hate': 'Hate Speech',
  'hate/threatening': 'Hate Speech (Threatening)',
  'illicit': 'Illicit Content',
  'illicit/violent': 'Illicit (Violent)',
  'self-harm': 'Self-Harm',
  'self-harm/intent': 'Self-Harm (Intent)',
  'self-harm/instructions': 'Self-Harm (Instructions)',
  'sexual': 'Sexual Content',
  'sexual/minors': 'Sexual Content (Minors)',
  'violence': 'Violence',
  'violence/graphic': 'Violence (Graphic)',
};

// ── Russian category labels (user-facing) ────────────────────
// The OpenAI categories above are admin/log-facing. The branded
// ModerationWarning banner shows these warm Russian labels instead.
export const CATEGORY_LABELS_RU: Record<string, string> = {
  'harassment': 'Оскорбления',
  'harassment/threatening': 'Угрозы',
  'hate': 'Разжигание ненависти',
  'hate/threatening': 'Ненависть с угрозами',
  'illicit': 'Противоправный контент',
  'illicit/violent': 'Противоправное насилие',
  'self-harm': 'Тема самоповреждения',
  'self-harm/intent': 'Тема самоповреждения',
  'self-harm/instructions': 'Тема самоповреждения',
  'sexual': 'Контент 18+',
  'sexual/minors': 'Недопустимый контент',
  'violence': 'Насилие',
  'violence/graphic': 'Сцены насилия',
};

/**
 * Russian label for a moderation category. Falls back to the parent category
 * (before the slash), then the English label, then the raw key — so an
 * unrecognised category never renders as a blank.
 */
export function categoryLabelRu(category: string): string {
  if (!category) return '';
  // The moderation backend emits both slash and underscore variants of the
  // same subcategory (e.g. `harassment/threatening` and `harassment_threatening`);
  // normalize underscores to slashes so both map to one Russian label.
  const normalized = category.replace(/_/g, '/');
  return (
    CATEGORY_LABELS_RU[normalized] ||
    CATEGORY_LABELS_RU[normalized.split('/')[0]] ||
    CATEGORY_LABELS[normalized] ||
    category
  );
}

/**
 * CORE-196 — ответ, когда проверить не удалось.
 *
 * Раньше здесь стоял flagged=false: сервис молчал, и содержимое уходило
 * дальше непроверенным, а единственным следом был console.warn. От этого
 * отказались — непроверенное не публикуем. Категория `moderation_unavailable`
 * отделяет «мы не смогли проверить» от «вы нарушили правила»: человеку надо
 * предложить повторить, а не обвинять его в нашем сбое.
 */
export const MODERATION_UNAVAILABLE_CATEGORY = 'moderation_unavailable';

const SERVICE_UNAVAILABLE: ModerationResult = {
  flagged: true,
  violations: [
    {
      category: MODERATION_UNAVAILABLE_CATEGORY,
      description: 'Проверка временно недоступна',
      score: 0,
    },
  ],
};

/** Это отказ из-за нашего сбоя, а не нарушение со стороны человека. */
export function isUnavailableResult(result: ModerationResult): boolean {
  return result.violations.some(
    (v) => v.category === MODERATION_UNAVAILABLE_CATEGORY,
  );
}

// ── API call ─────────────────────────────────────────────────

/**
 * Check text and/or image content against OpenAI moderation policies.
 *
 * При сетевой ошибке, таймауте или не-2xx возвращает отказ с категорией
 * `moderation_unavailable`: проверка обязательна, непроверенное не проходит.
 */
export async function checkContentModeration(params: {
  text?: string;
  imageUrl?: string;
}): Promise<ModerationResult> {
  const { text, imageUrl } = params;

  // Nothing to check — treat as safe.
  if (!text?.trim() && !imageUrl) {
    return { flagged: false, violations: [] };
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

  try {
    const response = await fetch(`${MODERATION_API_URL}/api/moderation/check`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        text: text?.trim() || undefined,
        image_url: imageUrl || undefined,
      }),
      signal: controller.signal,
    });

    if (!response.ok) {
      console.warn(`Moderation API returned ${response.status} — публикацию не пропускаем.`);
      return SERVICE_UNAVAILABLE;
    }

    const data = await response.json();
    return {
      flagged: data.flagged ?? false,
      violations: (data.violations ?? []).map((v: any) => ({
        category: v.category,
        description: v.description || CATEGORY_LABELS[v.category] || v.category,
        score: v.score,
      })),
    };
  } catch (err) {
    // Сеть, CORS или таймаут — проверить не удалось, значит не пропускаем.
    console.warn('Moderation check failed — публикацию не пропускаем:', err);
    return SERVICE_UNAVAILABLE;
  } finally {
    clearTimeout(timeoutId);
  }
}
